<template>
  <div class="w-100">
    <JobProfileSettingsSkeleton v-if="pageLoading" />

    <div
      v-else
      class="form-wrapper"
    >
      <section class="settings-section">
        <h2 class="text-h2 section-title mb-4">
          Profile Fields
        </h2>
        <p class="section-description mb-4">
          Control which fields appear on job profiles and set whether they are required. <br>
          You can drag to reorder how they appear in the editor.
          <a
            href="#"
            class="section-description-link"
            data-beacon-article-sidebar="69cd28049207e139315c3eb2"
            @click.stop
          >Learn more here</a>.
        </p>

        <div class="column-header field-columns">
          <span class="column-header-label field-col-field">Field</span>
          <span class="column-header-label field-col-visibility">Visibility</span>
          <span class="column-header-label field-col-status">Status</span>
        </div>

        <draggable
          v-model="fields"
          item-key="ulid"
          handle=".drag-handle"
          tag="div"
          class="draggable-list"
          ghost-class="ghost"
          :disabled="componentLoading"
          @end="handleDragEnd"
        >
          <template #item="{ element }">
            <div class="field-row">
              <div class="field-col-field field-name-cell">
                <button
                  type="button"
                  class="drag-handle"
                  aria-label="Reorder field"
                  :disabled="componentLoading"
                >
                  <v-icon
                    size="20"
                    icon="mdi-drag"
                  />
                </button>
                <span class="field-order text-body-3 text-textLight">{{ element.order + 1 }}</span>
                <span class="field-name">{{ element.name }}</span>
              </div>
              <div class="field-col-visibility">
                <DpplSlimSwitch
                  :model-value="!element.is_hidden"
                  :label="element.is_hidden ? 'Hide' : 'Show'"
                  active-color="brand"
                  :disabled="componentLoading"
                  a11y-label-text="Element visibility"
                  @update:model-value="handleFieldVisible(element.ulid, $event)"
                />
              </div>
              <div class="field-col-status">
                <DpplSlimSwitch
                  :model-value="element.is_required"
                  :disabled="element.is_hidden || componentLoading"
                  :label="element.is_required ? 'Required' : 'Not required'"
                  active-color="secondary"
                  a11y-label-text="Element required status"
                  @update:model-value="handleFieldRequired(element.ulid, $event)"
                />
              </div>
            </div>
          </template>
        </draggable>
      </section>

      <section class="settings-section">
        <h2 class="text-h2 section-title mb-4">
          Job Levels
        </h2>
        <p class="section-description mb-4">
          Manage your organization's job levels by customizing how each level is shortened.<br>
          You can drag to arrange them from most junior to most senior.
          <a
            href="#"
            class="section-description-link"
            data-beacon-article-sidebar="69cd28049207e139315c3eb2"
            @click.stop
          >Learn more here</a>.
        </p>

        <div class="column-header level-columns">
          <span class="column-header-label level-col-level">Level</span>
          <span class="column-header-label level-col-shortened">Shortened</span>
        </div>

        <draggable
          v-model="levels"
          item-key="ulid"
          handle=".drag-handle"
          tag="div"
          class="draggable-list"
          ghost-class="ghost"
          :disabled="componentLoading"
          @end="handleDragEnd"
        >
          <template #item="{ element }">
            <div class="level-row">
              <div class="level-col-level level-name-cell">
                <button
                  type="button"
                  class="drag-handle"
                  aria-label="Reorder level"
                  :disabled="componentLoading"
                >
                  <v-icon
                    size="20"
                    icon="mdi-drag"
                  />
                </button>
                <span class="field-order text-body-3 text-textLight">{{ element.order + 1 }}</span>
                <span class="level-name">{{ element.name }}</span>
              </div>
              <div class="level-col-shortened">
                <DpplTextField
                  class="level-alias-field"
                  label=""
                  :aria-label="`Shortened label for ${element.name}, max 5 characters`"
                  :model-value="element.short_name"
                  maxlength="5"
                  :disabled="componentLoading"
                  @update:model-value="setLevelAlias(element.ulid, $event)"
                />
              </div>
            </div>
          </template>
        </draggable>
      </section>

      <div class="mt-4 save-bar">
        <DpplAutoSizedButton
          :disabled="!isDirty || componentLoading || !!saveValidationMessage"
          :loading="componentLoading"
          @click="save"
        >
          Update
        </DpplAutoSizedButton>
        <p
          v-if="saveValidationMessage"
          class="text-body-3 validation-message"
          role="status"
        >
          {{ saveValidationMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import draggable from 'vuedraggable';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSlimSwitch from '@/components/datapeople/DpplSlimSwitch.vue';
import JobProfileSettingsSkeleton from '@/components/settings/components/JobProfileSettingsSkeleton.vue';
import { validateForSave } from '@/helpers/jobProfileSettingsValidation';
import { useAppLoading } from '@/stores/app/loading';
import { useJobProfileSettings } from '@/stores/settings/jobProfileSettings';

const jobProfileSettings = useJobProfileSettings();
const {
  fields,
  levels,
  isDirty,
} = storeToRefs(jobProfileSettings);
const {
  load,
  save,
  syncSortOrders,
  setFieldVisible,
  setFieldRequired,
  setLevelAlias,
} = jobProfileSettings;

const handleFieldVisible = (id, val) => {
  setFieldVisible(id, val);
};

const handleFieldRequired = (id, val) => {
  setFieldRequired(id, val);
};

const handleDragEnd = () => {
  syncSortOrders();
};

const appLoadingStore = useAppLoading();
const { componentLoading, pageLoading } = storeToRefs(appLoadingStore);

const saveValidationMessage = computed(() => {
  const { ok, errors } = validateForSave({
    fields: fields.value,
    levels: levels.value,
  });

  if (ok) return '';

  return errors[0] || '';
});

onMounted(async () => {
  await load();
});
</script>

<style scoped lang="less">
.form-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  border-radius: 10px;
  background: @white;
  padding: 40px;
}

.settings-section {
  width: 100%;
}

.section-title {
  color: @text-darkest;
}

.section-description {
  max-width: 100%;
  font-size: 16px;
  font-weight: 500 !important;
  line-height: 1.45;
  color: @text-dark;
}

.section-description-link {
  color: @text-brand;
  font-weight: 500 !important;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    opacity: 0.8;
  }
}

.column-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  width: 100%;
  margin-bottom: 8px;
}

.column-header-label {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
  color: @text-dark;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.field-columns {
  .field-col-field {
    flex: 0 0 300px;
    width: 300px;
  }

  .field-col-visibility {
    flex: 0 0 100px;
    width: 100px;
  }

  .field-col-status {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.level-columns {
  .level-col-level {
    flex: 0 0 400px;
    width: 400px;
  }

  .level-col-shortened {
    flex: 0 0 100px;
    width: 100px;
  }
}

.draggable-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.field-row,
.level-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
}

.field-row {
  margin-bottom: 16px;

  .field-col-field {
    flex: 0 0 300px;
    width: 300px;
  }

  .field-col-visibility {
    flex: 0 0 100px;
    width: 100px;
  }

  .field-col-status {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.level-row {
  .level-col-level {
    flex: 0 0 400px;
    width: 400px;
  }

  .level-col-shortened {
    flex: 0 0 100px;
    width: 100px;
  }
}

.field-name-cell,
.level-name-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.field-order {
  min-width: 1.25rem;
  flex-shrink: 0;
}

.field-name,
.level-name {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: @text-darkest;
  min-width: 0;
}

.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: grab;
  color: @text-light;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }

  &:disabled {
    cursor: default;
    color: @text-light;
    opacity: 0.5;
  }
}

.field-col-visibility,
.field-col-status {
  display: flex;
  align-items: center;
}

.level-alias-field {
  width: 100%;
  max-width: 100px;
  min-width: 100px;

  :deep(.form-label-text) {
    display: none;
  }

  :deep(.v-text-field) {
    margin-top: 0;
  }

  :deep(.v-field) {
    border-radius: 8px;
    background-color: transparent;
    --v-field-border-opacity: 1;
    color: @stroke-default !important;

    .v-field__input {
      padding: 0;
    }
  }

  :deep(input) {
    min-height: 40px;
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.5;
    color: @text-darkest;
  }
}

.save-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  width: 100%;
}

.validation-message {
  margin: 0;
  color: @error;
}

:deep(.ghost) {
  opacity: 0.55;
}
</style>
